import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getPersisted, PersistKeys, setPersisted } from "./persistence";

interface SavedRoute {
  pathname: string;
}

const PERSISTABLE_PREFIXES = ["/members"];

function isPersistable(pathname: string) {
  return PERSISTABLE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/**
 * Keeps the "last meaningful route" in localStorage so that when the Chrome
 * extension iframe is destroyed (side panel close) and recreated on reopen,
 * we can drop the user back where they were instead of resetting to `/`.
 *
 * PHI constraint: only the pathname is persisted. Router `location.state`
 * can carry search filters (first/last name, MPI, DOB) when navigating from
 * results to a profile — that data must never land in localStorage. The
 * destination route fetches member data fresh via GraphQL on mount, so
 * dropping state has no functional cost for the reopen flow.
 */
export function RoutePersister() {
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  // One-shot restore on mount.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const saved = getPersisted<SavedRoute | null>(PersistKeys.lastRoute, null);
    if (!saved?.pathname) return;

    const atRoot = location.pathname === "/" || location.pathname === "/members";
    if (!atRoot) return;
    if (saved.pathname === location.pathname) return;

    navigate(saved.pathname, { replace: true });
    // Intentionally mount-only — deps list omitted on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on every persistable location change.
  useEffect(() => {
    if (!isPersistable(location.pathname)) return;
    setPersisted<SavedRoute>(PersistKeys.lastRoute, {
      pathname: location.pathname,
    });
  }, [location.pathname]);

  return null;
}
